import { useState } from 'react';
import { MessageSquareText } from 'lucide-react';
import type { Profile } from '@/engine/types';
import { isLlmOn } from '@/features/llm/isLlmOn';
import { saveProfile } from '@/features/profile/useProfile';
import { nowIso } from '@/lib/db';
import { isSupabaseConfigured } from '@/lib/supabase';
import { cn } from '@/lib/utils';

/**
 * Interruptor de la capa LLM (SPEC 8).
 *
 * Apagado por defecto y con su PROPIO consentimiento, separado del de datos de
 * salud: es el único sitio de la app por el que un texto sale de la Unión
 * Europea. Activarlo exige leer qué se envía y a quién; desactivarlo es un
 * toque y no pregunta nada.
 */
export function LlmSettings({ profile }: { profile: Profile }) {
  const [confirming, setConfirming] = useState(false);
  const enabled = isLlmOn(profile);

  function disable() {
    void saveProfile({ ...profile, llmEnabled: false, llmConsentAt: null });
  }

  function enable() {
    void saveProfile({ ...profile, llmEnabled: true, llmConsentAt: nowIso() });
    setConfirming(false);
  }

  return (
    <section aria-labelledby="asistente" className="flex flex-col gap-2">
      <h2 id="asistente" className="text-sm font-semibold">
        Escribir comidas con texto libre
      </h2>
      <p className="-mt-1 text-xs text-[var(--color-text-muted)]">
        Opcional. Te deja escribir «200 g de arroz con pollo y una manzana» en vez de buscar cada
        cosa. Los nutrientes salen siempre de la base de datos de la app, nunca del asistente.
      </p>

      {!isSupabaseConfigured ? (
        <p className="text-xs text-[var(--color-text-muted)]">
          No disponible: hace falta configurar el servidor (ver SETUP.md).
        </p>
      ) : confirming ? (
        <div
          role="dialog"
          aria-modal="false"
          aria-label="Activar el texto libre"
          className="flex flex-col gap-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface-2)] p-3 text-sm"
        >
          <p className="font-medium">Antes de activarlo, esto es lo que pasa:</p>
          <ul className="flex list-disc flex-col gap-1 pl-5 text-xs text-[var(--color-text-muted)]">
            <li>
              Se envían dos cosas: el texto que escribas en la caja de «Añadir» y el texto de las
              sugerencias de la app, para redactarlas de forma más natural. Las sugerencias pueden
              mencionar nutrientes, cantidades o tu entrenamiento.
            </li>
            <li>Nunca se envían tu nombre, tu correo ni ningún identificador tuyo.</li>
            <li>
              Lo procesa <strong>Groq</strong>, con servidores en <strong>Estados Unidos</strong>.
              Es el único punto de la app por el que algo sale de la Unión Europea.
            </li>
            <li>Groq no usa lo que envías para entrenar sus modelos.</li>
            <li>Puedes desactivarlo cuando quieras con un toque.</li>
          </ul>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setConfirming(false)}
              className="tap flex-1 rounded-xl border border-[var(--color-border)] px-4 text-sm font-medium"
            >
              Mejor no
            </button>
            <button
              type="button"
              onClick={enable}
              className="tap flex-1 rounded-xl bg-[var(--color-brand-button)] px-4 text-sm font-semibold text-[var(--color-on-brand)]"
            >
              Lo entiendo, activar
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          aria-labelledby="asistente"
          onClick={() => (enabled ? disable() : setConfirming(true))}
          className={cn(
            'tap flex items-center justify-between gap-2 rounded-xl border px-3 text-sm font-medium',
            enabled
              ? 'border-[var(--color-brand)] bg-[var(--color-brand-soft)] text-[var(--color-brand-strong)]'
              : 'border-[var(--color-border)]',
          )}
        >
          <span className="flex items-center gap-2">
            <MessageSquareText aria-hidden="true" className="size-5 shrink-0" />
            {enabled ? 'Activado' : 'Desactivado'}
          </span>
        </button>
      )}
    </section>
  );
}
